import {
  AssistantRuntimeProvider,
  useAui,
  useAuiState,
  useExternalStoreRuntime,
  type AppendMessage,
  type ExternalThreadQueueAdapter,
} from "@assistant-ui/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSelector, useStore } from "react-redux";

import { agentClient } from "../agentClient";
import { useAgentTarget } from "../hooks/useAgentTarget";
import type { TabView } from "../protocol/protocol";
import type { AgentState } from "../store/agentSlice";
import { makeSelectRuntimeInput, type RuntimeInput } from "../store/selectors";
import type { Target, TargetKey } from "../store/targets";
import {
  createAttachmentAdapter,
  settleHeldFiles,
  threadFileId,
  type HeldFiles,
} from "./attachments";
import { convertMessage } from "./convert";

const NO_ITEMS: ExternalThreadQueueAdapter["items"] = [];

function textOf(message: AppendMessage): string {
  return message.content
    .flatMap((part) => (part.type === "text" ? [part.text] : []))
    .join("\n\n")
    .trim();
}

/**
 * The adapter callbacks, built once per view and reading the view's current thread through a ref
 * (and, at send, the pages picked for its PDFs).
 */
function createCallbacks(
  latest: { current: RuntimeInput },
  held: HeldFiles,
  pagePicks: () => AgentState["pagePicks"]
) {
  const send = async (message: AppendMessage) => {
    const threadId = latest.current.threadId;
    if (threadId == null) return;
    const files = (message.attachments ?? []).flatMap((attachment) => {
      const id = threadFileId(attachment.id);
      const contentType = attachment.contentType ?? "";
      return id == null ? [] : [{ id, name: attachment.name, contentType }];
    });
    const text = textOf(message);
    if (text || files.length) agentClient.send(threadId, text, files, pagePicks()[threadId]);
  };
  // The server queues a message sent during a run (a pending row), so the queue sends at once:
  // Send stays live while the assistant answers.
  const queue: ExternalThreadQueueAdapter = {
    items: NO_ITEMS,
    steerItems: NO_ITEMS,
    enqueue: (message) => void send(message),
    steer: (message) => void send(message),
    move: () => {},
    edit: () => {},
    remove: () => {},
  };
  return {
    send,
    queue,
    onCancel: async () => {
      const threadId = latest.current.threadId;
      if (threadId != null) await agentClient.cancel(threadId);
    },
    onLoadEarlier: async () => {
      const threadId = latest.current.threadId;
      if (threadId != null) await agentClient.loadEarlier(threadId);
    },
    attachments: createAttachmentAdapter(() => latest.current.threadId, held),
  };
}

/**
 * One assistant-ui runtime for one view (the lip card, /assistant), keyed by the thread it shows:
 * a new thread mounts a new runtime. The thread-switch spike measured the alternative (one runtime,
 * `adapters.threadList.threadId` switched) painting one or two frames of the old thread's messages
 * under the new one on nearly every switch; a keyed runtime painted none.
 */
export function AgentRuntime({
  target,
  view,
  children,
}: {
  target: Target;
  /** The in-page card or the /assistant page, for the other tabs' dots. */
  view: TabView;
  children: ReactNode;
}) {
  const key = useAgentTarget(target);
  const threadId = useSelector(
    (state: { agent: AgentState }) => state.agent.targets[key]?.threadId ?? null
  );
  const lost = useSelector((state: { agent: AgentState }) => !!state.agent.targets[key]?.lost);
  // Files picked before the thread is known: they outlive the runtime they were picked in.
  const [held] = useState<HeldFiles>(() => new Map());
  useEffect(() => {
    if (threadId != null) agentClient.reportPresence({ thread: threadId, view });
  }, [threadId, view]);
  useEffect(() => {
    if (lost) settleHeldFiles(held, "lost");
  }, [held, lost]);
  useEffect(() => () => void settleHeldFiles(held, "lost"), [held]);
  return (
    <AgentThreadRuntime key={threadId ?? "none"} targetKey={key} held={held}>
      {children}
    </AgentThreadRuntime>
  );
}

/**
 * The runtime over what the store holds for a target, with no subscription or presence of its
 * own: AgentRuntime's inside. Mount it bare only for threads the socket must not see (the DEV
 * fixtures), keyed by the thread like AgentRuntime does.
 */
export function AgentThreadRuntime({
  targetKey,
  held: heldFiles,
  children,
}: {
  targetKey: TargetKey;
  /** The view's files waiting for a thread (AgentRuntime's); a bare runtime holds its own. */
  held?: HeldFiles;
  children: ReactNode;
}) {
  const selectInput = useMemo(makeSelectRuntimeInput, []);
  const input = useSelector((state: { agent: AgentState }) => selectInput(state, targetKey));
  const latest = useRef(input);
  latest.current = input;
  const [ownHeld] = useState<HeldFiles>(() => new Map());
  const held = heldFiles ?? ownHeld;
  const store = useStore<{ agent: AgentState }>();
  const callbacks = useMemo(
    () => createCallbacks(latest, held, () => store.getState().agent.pagePicks),
    [held, store]
  );
  // Fixed for this runtime's life; it names the thread to the runtime (threadListItem.id).
  const [threadList] = useState(() => ({
    threadId: input.threadId == null ? undefined : String(input.threadId),
  }));

  const runtime = useExternalStoreRuntime({
    messages: input.messages,
    convertMessage,
    isRunning: input.isRunning,
    isDisabled: input.threadId == null,
    hasEarlier: input.hasEarlier,
    onLoadEarlier: callbacks.onLoadEarlier,
    onNew: callbacks.send,
    queue: callbacks.queue,
    onCancel: callbacks.onCancel,
    adapters: { attachments: callbacks.attachments, threadList },
  });

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <AddHeldFiles held={held} threadId={input.threadId} />
      {children}
    </AssistantRuntimeProvider>
  );
}

/**
 * Adds the files held for a thread to this runtime's composer, where they upload, once the runtime
 * shows the thread (it builds the thread, with an empty composer, in an effect after its mount).
 */
function AddHeldFiles({ held, threadId }: { held: HeldFiles; threadId: number | null }) {
  const aui = useAui();
  const shown = useAuiState((state) => state.threadListItem.id);
  useLayoutEffect(() => {
    if (threadId == null || shown !== String(threadId) || held.size === 0) return;
    for (const { file } of settleHeldFiles(held, "handed")) void aui.composer.addAttachment(file);
  }, [aui, held, threadId, shown]);
  return null;
}
